import type { 
  MarketCondition, 
  MarketConditionConfig, 
  MarketAnalysis, 
  MarketComparisonData,
  ScenarioInput 
} from '@/types/supremeIntelligenceTypes';
import { calculateMortgagePayment } from '@/components/calculator/supreme-intelligence/utils/mortgageUtils';

export const MARKET_CONDITIONS: Record<MarketCondition, MarketConditionConfig> = {
  'base': {
    name: 'Current Market',
    description: 'Current rates and typical 3% appreciation',
    rateAdjustment: 0,
    appreciationRate: 3,
    icon: 'TrendingUp',
    color: 'hsl(var(--primary))'
  },
  'rising-rates': {
    name: 'Rising Rates',
    description: '+1% rate increase, slower appreciation',
    rateAdjustment: 1,
    appreciationRate: 2,
    icon: 'ArrowUp',
    color: 'hsl(var(--destructive))'
  },
  'falling-rates': {
    name: 'Falling Rates',
    description: '-1% rate decrease, stronger appreciation',
    rateAdjustment: -1,
    appreciationRate: 4,
    icon: 'ArrowDown',
    color: 'hsl(var(--chart-2))'
  },
  'high-appreciation': {
    name: 'Hot Market',
    description: 'Current rates, 6% annual appreciation',
    rateAdjustment: 0,
    appreciationRate: 6,
    icon: 'Flame',
    color: 'hsl(var(--chart-1))'
  },
  'low-appreciation': {
    name: 'Slow Market',
    description: 'Current rates, 1% appreciation',
    rateAdjustment: 0,
    appreciationRate: 1,
    icon: 'CloudDrizzle',
    color: 'hsl(var(--muted-foreground))'
  },
  'recession': {
    name: 'Recession',
    description: '+0.5% rates, -2% home value decline',
    rateAdjustment: 0.5,
    appreciationRate: -2,
    icon: 'TrendingDown',
    color: 'hsl(var(--destructive))'
  }
};

export const simulateMarketCondition = (
  scenario: ScenarioInput,
  condition: MarketCondition
): MarketAnalysis => {
  const config = MARKET_CONDITIONS[condition];
  const adjustedRate = scenario.interestRate + config.rateAdjustment;
  
  const downPaymentAmount = scenario.homePrice * (scenario.downPaymentPercentage / 100);
  const loanAmount = scenario.homePrice - downPaymentAmount;
  
  // Calculate adjusted monthly payment
  const monthlyPI = calculateMortgagePayment(loanAmount, adjustedRate, scenario.loanTerm);
  const monthlyTax = (scenario.homePrice * scenario.propertyTaxRate / 100) / 12;
  const monthlyInsurance = scenario.homeInsurance / 12;
  const monthlyHOA = scenario.hoaFees;
  
  // PMI if down payment < 20%
  const monthlyPMI = scenario.downPaymentPercentage < 20 
    ? loanAmount * 0.005 / 12 
    : 0;
  
  const adjustedMonthlyPayment = monthlyPI + monthlyTax + monthlyInsurance + monthlyHOA + monthlyPMI;
  
  // Calculate equity at different years
  const calculateEquityAtYear = (years: number): { equity: number; homeValue: number } => {
    const homeValue = scenario.homePrice * Math.pow(1 + config.appreciationRate / 100, years);
    
    // Calculate remaining loan balance
    const monthlyRate = adjustedRate / 100 / 12;
    const totalMonths = scenario.loanTerm * 12;
    const monthsPassed = years * 12;
    
    if (monthsPassed >= totalMonths) {
      return { equity: homeValue, homeValue };
    }
    
    const remainingBalance = loanAmount * (
      (Math.pow(1 + monthlyRate, totalMonths) - Math.pow(1 + monthlyRate, monthsPassed)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1)
    );
    
    const equity = Math.max(0, homeValue - remainingBalance);
    return { equity, homeValue };
  };
  
  const year5 = calculateEquityAtYear(5);
  const year10 = calculateEquityAtYear(10);
  const year30 = calculateEquityAtYear(30);
  
  // Calculate total interest paid
  const totalPayments = adjustedMonthlyPayment * scenario.loanTerm * 12;
  const totalInterestPaid = totalPayments - loanAmount;
  
  return {
    condition,
    scenarioId: scenario.id,
    scenarioName: scenario.name,
    adjustedMonthlyPayment: Math.round(adjustedMonthlyPayment),
    adjustedTotalCost: Math.round(totalPayments + downPaymentAmount),
    equityAtYear5: Math.round(year5.equity),
    equityAtYear10: Math.round(year10.equity),
    equityAtYear30: Math.round(year30.equity),
    homeValueAtYear30: Math.round(year30.homeValue),
    totalInterestPaid: Math.round(totalInterestPaid)
  };
};

export const generateMarketComparisonData = (
  scenarios: ScenarioInput[]
): MarketComparisonData => {
  const conditions = Object.keys(MARKET_CONDITIONS) as MarketCondition[];
  const analyses: MarketAnalysis[] = [];
  
  // Generate analysis for each scenario under each condition
  scenarios.forEach(scenario => {
    conditions.forEach(condition => {
      analyses.push(simulateMarketCondition(scenario, condition));
    });
  });
  
  // Find best performers for each condition
  const bestPerformers: { [condition: string]: string } = {};
  conditions.forEach(condition => {
    const conditionAnalyses = analyses.filter(a => a.condition === condition);
    if (conditionAnalyses.length > 0) {
      // Best = lowest total cost
      const best = conditionAnalyses.reduce((prev, curr) => 
        curr.adjustedTotalCost < prev.adjustedTotalCost ? curr : prev
      );
      bestPerformers[condition] = best.scenarioId;
    }
  });
  
  return {
    scenarios,
    conditions: conditions.map(c => MARKET_CONDITIONS[c]),
    analyses,
    bestPerformers
  };
};
